/**
 * Owner activity feed — pure derivation, no queries.
 *
 * Two sources, in this order of authority:
 *
 *   1. `audit_logs` rows written by src/lib/owner-audit.ts — one row per action.
 *      This is the real history: two sends of the same invoice are two rows.
 *   2. State derived from the invoice/document/submission records themselves.
 *
 * (2) alone is what this file used to be, and it is structurally lossy: an
 * invoice carries ONE `sent_at`, so re-sending it overwrote the timestamp and
 * the earlier send disappeared from the timeline instead of joining it. The
 * derived events are kept only for history recorded before the log existed —
 * per invoice, per kind, a logged event always wins, so nothing renders twice.
 *
 * Kept separate from the component so it is unit-testable and so the timeline
 * file only exports a component.
 */
import {
  Banknote,
  Building2,
  CheckCircle2,
  FileText,
  Paperclip,
  Pencil,
  Receipt,
  Send,
  ShieldCheck,
  StickyNote,
  Trash2,
  Upload,
  UserPlus,
  ArrowLeftRight,
} from "lucide-react";
import { formatEUR } from "@/lib/v2-format";
import type { ManagementInvoiceRow } from "@/hooks/v2/useManagementInvoices";
import type {
  OwnerAuditRow,
  OwnerDoc,
  OwnerSubmission,
} from "@/hooks/v2/useOwnerDetail";
import type { NoteRow } from "@/hooks/v2/useEntityNotes";
import {
  ownerActorLabel,
  ownerAuditActionTitle,
  ownerMirroredActionTitle,
  ownerFieldLabel,
  ownerFieldValue,
} from "@/lib/owner-audit-titles";

export type Tone = "teal" | "emerald" | "amber" | "rose" | "sky" | "slate";

export type OwnerActivityEvent = {
  /** ISO timestamp the event happened at — the sort key. */
  at: string;
  icon: typeof Receipt;
  tone: Tone;
  title: string;
  /** Free-text detail: the note itself, a deletion reason, a document name. */
  detail?: string | null;
  /** Which invoice / document the event belongs to. */
  context?: string | null;
  /** Who did it. Only logged events know this; derived state does not. */
  actor?: string | null;
};

/**
 * How a logged action renders, and which derived event it replaces.
 *
 * `covers` is the whole point of the table: once an action has a real log row
 * for an invoice, the state-derived stand-in for that same fact must not also
 * render, or the newest send would appear twice.
 */
const LOGGED_ACTIONS: Record<
  string,
  { icon: typeof Receipt; tone: Tone; covers?: DerivedKind | DerivedKind[] }
> = {
  management_invoice_sent: { icon: Send, tone: "sky", covers: "sent" },
  management_invoice_marked_paid: {
    icon: CheckCircle2,
    tone: "emerald",
    covers: "paid",
  },
  management_invoice_marked_unpaid: { icon: Receipt, tone: "amber" },
  management_invoice_proof_uploaded: {
    icon: Upload,
    tone: "amber",
    covers: "proof",
  },
  management_invoice_proof_deleted: {
    icon: Trash2,
    tone: "rose",
    covers: "proof_deleted",
  },
  management_invoice_note_added: {
    icon: StickyNote,
    tone: "teal",
    covers: "note",
  },
  // Confirming an offset is ONE write that marks the invoice paid AND files
  // the note recording the deduction (confirmOffset in useManagementInvoices),
  // so it stands in for both derived facts. Listing only one of them left the
  // feed showing a single confirmation three times: this entry, plus "Invoice
  // marked paid", plus "Payment note added".
  management_invoice_offset_confirmed: {
    icon: ArrowLeftRight,
    tone: "sky",
    covers: ["paid", "note"],
  },
  owner_updated: { icon: Pencil, tone: "slate" },
  owner_login_link_sent: { icon: Send, tone: "sky" },
  owner_document_uploaded: { icon: FileText, tone: "slate", covers: "doc" },
  owner_document_deleted: { icon: Trash2, tone: "rose" },
  // Written by the database triggers, which see writers the TypeScript path
  // never could — the owner's own Settings page above all.
  owner_created: { icon: UserPlus, tone: "emerald" },
  owner_deleted: { icon: Trash2, tone: "rose" },
  owner_account_updated: { icon: Pencil, tone: "slate" },
  owner_payout_details_updated: { icon: Banknote, tone: "amber" },
  owner_property_link_added: { icon: ShieldCheck, tone: "emerald" },
  owner_property_link_removed: { icon: Trash2, tone: "rose" },
  owner_property_link_updated: { icon: Pencil, tone: "slate" },
  // The owner's portfolio, subscription and onboarding — see 20260817130000.
  owner_apartment_added: { icon: Building2, tone: "emerald" },
  owner_apartment_removed: { icon: Building2, tone: "rose" },
  owner_apartment_updated: { icon: Building2, tone: "slate" },
  owner_property_added: { icon: Building2, tone: "emerald" },
  owner_property_removed: { icon: Building2, tone: "rose" },
  owner_property_updated: { icon: Building2, tone: "slate" },
  owner_subscription_added: { icon: Banknote, tone: "emerald" },
  owner_subscription_removed: { icon: Banknote, tone: "rose" },
  owner_subscription_updated: { icon: Banknote, tone: "amber" },
  owner_onboarding_added: { icon: Send, tone: "sky" },
  owner_onboarding_removed: { icon: Trash2, tone: "rose" },
  owner_onboarding_updated: { icon: ShieldCheck, tone: "slate" },
  owner_onboarding_submission_added: { icon: ShieldCheck, tone: "emerald" },
  owner_onboarding_submission_removed: { icon: Trash2, tone: "rose" },
  owner_onboarding_submission_updated: { icon: ShieldCheck, tone: "slate" },
  owner_apartment_document_added: { icon: FileText, tone: "slate" },
  owner_apartment_document_removed: { icon: Trash2, tone: "rose" },
  owner_apartment_document_updated: { icon: FileText, tone: "slate" },
  owner_account_document_added: { icon: FileText, tone: "slate" },
  owner_account_document_removed: { icon: Trash2, tone: "rose" },
  owner_account_document_updated: { icon: FileText, tone: "slate" },
};

/**
 * Actions whose point IS the before/after.
 *
 * "Portal account updated" on its own tells an operator nothing — the same
 * complaint that put this trail in. The rest carry values for the record (an
 * invoice number, a document type) rather than for the reader, and printing
 * them would put "doc_type: — → other" under a line already saying "Document
 * uploaded".
 */
const DIFF_ACTIONS = new Set([
  "owner_updated",
  "owner_account_updated",
  "owner_payout_details_updated",
  "owner_property_link_updated",
  "owner_apartment_updated",
  "owner_property_updated",
  "owner_subscription_updated",
  "owner_onboarding_updated",
  "owner_onboarding_submission_updated",
  "owner_apartment_document_updated",
  "owner_account_document_updated",
]);

type DerivedKind = "sent" | "paid" | "proof" | "proof_deleted" | "note" | "doc";

/**
 * What a logged row says beyond its title.
 *
 * A field edit is only meaningful as a before/after — "Owner details updated"
 * on its own tells an operator nothing, which is exactly the complaint that
 * put this whole trail in. Everything else carries whatever free text the
 * action recorded (a payment note, a deletion reason).
 */
function loggedDetail(row: OwnerAuditRow): string | null {
  const meta = (row.metadata ?? {}) as Record<string, unknown>;
  if (typeof meta.note === "string" && meta.note.trim())
    return meta.note.trim();
  if (typeof meta.reason === "string" && meta.reason.trim())
    return meta.reason.trim();

  if (!DIFF_ACTIONS.has(row.action)) return null;
  const next = (row.new_values ?? null) as Record<string, unknown> | null;
  const prev = (row.old_values ?? null) as Record<string, unknown> | null;
  if (!next) return null;
  const lines = Object.keys(next).map(
    (field) =>
      `${ownerFieldLabel(field)}: ${ownerFieldValue(prev?.[field])} → ${ownerFieldValue(next[field])}`
  );
  return lines.length ? lines.join("\n") : null;
}

function invoiceLabel(inv: ManagementInvoiceRow): string {
  const period = inv.period_month
    ? new Date(`${String(inv.period_month).slice(0, 10)}T12:00:00`)
    : null;
  const month =
    period && !Number.isNaN(period.getTime())
      ? period.toLocaleDateString("en-GB", { month: "short", year: "numeric" })
      : null;
  return [inv.invoice_no, month].filter(Boolean).join(" · ") || "Invoice";
}

/**
 * Flatten every dated fact into one feed. Anything without a timestamp is
 * dropped rather than guessed at — an undated entry in a timeline is noise.
 */
export function buildOwnerActivity({
  invoices,
  documents,
  submission,
  subscriptionStartedAt,
  ownerCreatedAt,
  auditLogs = [],
  notes = [],
}: {
  invoices: ManagementInvoiceRow[];
  documents: OwnerDoc[];
  submission: OwnerSubmission | null;
  subscriptionStartedAt?: string | null;
  ownerCreatedAt?: string | null;
  /** Logged actions — the append-only half of the feed. */
  auditLogs?: OwnerAuditRow[];
  /** Internal notes and their attachments, flattened — see flattenNotes(). */
  notes?: NoteRow[];
}): OwnerActivityEvent[] {
  const events: OwnerActivityEvent[] = [];
  const push = (e: OwnerActivityEvent | null) => {
    if (e?.at) events.push(e);
  };

  // ── Logged actions ───────────────────────────────────────────────────────
  // Every row becomes its own event, unconditionally. This is what makes the
  // feed a thread: a second send does not replace the first, it follows it.
  const invoiceById = new Map(invoices.map((i) => [String(i.id), i]));
  const covered = new Set<string>();
  const coverKey = (
    kind: DerivedKind,
    id: string,
    /**
     * Discriminator for facts the record ALREADY stores append-only — proof
     * deletions live in a metadata array, so suppressing the whole kind would
     * erase every deletion that predates the log. Those are matched one by one,
     * on the timestamp the deletion was filed under.
     */
    occurrence?: string | null
  ) => `${kind}:${id}${occurrence ? `:${occurrence}` : ""}`;
  const cover = (
    kind: DerivedKind,
    id: string | null | undefined,
    occurrence?: string | null
  ) => {
    if (id) covered.add(coverKey(kind, id, occurrence));
  };
  const isCovered = (
    kind: DerivedKind,
    id: string | null | undefined,
    occurrence?: string | null
  ) => !!id && covered.has(coverKey(kind, id, occurrence));

  for (const row of auditLogs) {
    const spec = LOGGED_ACTIONS[row.action];
    // An unknown action is still real history — render it under its own name
    // rather than dropping it, and let owner-audit-titles.test.ts catch the
    // missing title.
    const meta = (row.metadata ?? {}) as Record<string, unknown>;
    // A change mirrored onto the owner's other record is ONE action, recorded
    // once. The title has to say both moved, or an entry reading "Account
    // updated" would suggest the owner record — and the next invoice — still
    // carried the old details.
    const title =
      (meta.mirrored === true ? ownerMirroredActionTitle(row.action) : null) ??
      ownerAuditActionTitle(row.action) ??
      row.action;
    const inv = invoiceById.get(String(row.resource_id ?? ""));
    const context = inv
      ? invoiceLabel(inv)
      : typeof meta.document_title === "string"
        ? meta.document_title
        : null;

    const covers = spec?.covers
      ? Array.isArray(spec.covers)
        ? spec.covers
        : [spec.covers]
      : [];
    for (const kind of covers) {
      if (kind === "doc") cover("doc", String(row.resource_id ?? ""));
      else if (kind === "proof_deleted")
        cover(
          "proof_deleted",
          inv ? String(inv.id) : null,
          typeof meta.deletion_at === "string" ? meta.deletion_at : null
        );
      else cover(kind, inv ? String(inv.id) : null);
    }

    push({
      at: row.created_at,
      icon: spec?.icon ?? Receipt,
      tone: spec?.tone ?? "slate",
      title:
        row.action === "management_invoice_sent" && inv
          ? `Invoice sent to owner — ${formatEUR((inv.amount_cents || 0) / 100)}`
          : title,
      detail: loggedDetail(row),
      context,
      // Name AND the capacity they acted in. "Pascal Fehst (Owner)" against
      // "Vishnu (Admin)" is the whole reason an operator opens this tab when
      // something changed and nobody remembers doing it. The role comes off the
      // row rather than a live lookup, so it stays what it was at the time.
      actor: ownerActorLabel(
        row.actor?.name ?? row.actor?.email ?? null,
        (row.metadata as Record<string, unknown> | null)?.actor_role
      ),
    });
  }

  for (const inv of invoices) {
    const context = invoiceLabel(inv);
    const amount = formatEUR((inv.amount_cents || 0) / 100);
    const meta = (inv.metadata ?? {}) as Record<string, unknown>;

    push({
      at: inv.created_at,
      icon: Receipt,
      tone: "slate",
      title: `Invoice issued — ${amount}`,
      context,
    });

    // Each derived event below stands in for a fact the log now records
    // properly. Where a log row exists for this invoice, it has already been
    // pushed above — with every occurrence, not just the last — so the
    // single-valued column must stay quiet.
    const id = String(inv.id);

    if (inv.sent_at && !isCovered("sent", id))
      push({
        at: inv.sent_at,
        icon: Send,
        tone: "sky",
        title: "Invoice sent to owner",
        context,
      });

    if (inv.proof_uploaded_at && !isCovered("proof", id))
      push({
        at: inv.proof_uploaded_at,
        icon: Upload,
        tone: "amber",
        title: "Payment proof uploaded",
        context,
      });

    // The mandatory note captured against a proof — what this tab exists for.
    const note = meta.proof_note;
    const noteAt = meta.proof_note_at;
    if (
      typeof note === "string" &&
      note.trim() &&
      typeof noteAt === "string" &&
      !isCovered("note", id)
    )
      push({
        at: noteAt,
        icon: StickyNote,
        tone: "teal",
        title: "Payment note added",
        detail: note.trim(),
        context,
      });

    // Every proof removal keeps its reason — the audit trail deleteProof writes.
    const deletions = Array.isArray(meta.proof_deletions)
      ? (meta.proof_deletions as Array<Record<string, unknown>>)
      : [];
    for (const d of deletions) {
      if (typeof d?.at !== "string") continue;
      // Matched deletion by deletion: the logged copy names who did it, so it
      // wins — but a deletion recorded before the log existed still renders.
      if (isCovered("proof_deleted", id, d.at)) continue;
      push({
        at: d.at,
        icon: Trash2,
        tone: "rose",
        title: "Payment proof deleted",
        detail: typeof d.reason === "string" ? d.reason : null,
        context,
      });
    }

    if (inv.paid_at && !isCovered("paid", id))
      push({
        at: inv.paid_at,
        icon: CheckCircle2,
        tone: "emerald",
        title: `Invoice marked paid — ${amount}`,
        context,
      });
  }

  for (const d of documents) {
    // An upload logged under its storage path already rendered above, with the
    // admin who did it attached. The row itself is the fallback for uploads
    // that predate the log.
    if (isCovered("doc", d.file_url)) continue;
    push({
      at: d.created_at,
      icon: FileText,
      tone: "slate",
      title: "Document uploaded",
      detail: d.notes?.trim() || null,
      context: d.title,
    });
  }

  // Notes are activity too. What an admin wrote about this owner, and the files
  // they attached, happened on a date and by a person exactly like every other
  // entry — leaving them only inside the drawer meant the tab claimed to be the
  // history while a whole surface of it happened elsewhere.
  //
  // One event per note, never two: a note carrying both text and a file is a
  // single thing that happened, filed under the file (the more findable half).
  for (const n of notes) {
    const body = n.body?.trim() || null;
    if (!body && !n.file_url) continue;
    push({
      at: n.created_at,
      icon: n.file_url ? Paperclip : StickyNote,
      tone: "slate",
      title: n.file_url
        ? "Internal document attached"
        : n.parent_id
          ? "Reply on internal note"
          : "Internal note added",
      detail: body,
      context: n.file_url ? (n.file_name ?? "Attachment") : null,
      actor: n.author?.name ?? n.author?.email ?? null,
    });

    // Resolving a thread is a decision — "we have handled this" — and the tab
    // is where anyone reconstructing what happened will look for it.
    if (n.resolved_at)
      push({
        at: n.resolved_at,
        icon: CheckCircle2,
        tone: "emerald",
        title: "Internal note resolved",
        detail: body,
        actor: n.resolver?.name ?? n.resolver?.email ?? null,
      });
  }

  if (submission?.submitted_at)
    push({
      at: submission.submitted_at,
      icon: ShieldCheck,
      tone: "sky",
      title: "Onboarding submitted",
      context: submission.reference_number,
    });
  if (submission?.verified_at)
    push({
      at: submission.verified_at,
      icon: ShieldCheck,
      tone: "emerald",
      title: "Onboarding verified",
      context: submission.reference_number,
    });

  if (subscriptionStartedAt)
    push({
      at: subscriptionStartedAt,
      icon: Banknote,
      tone: "teal",
      title: "Subscription started",
    });

  if (ownerCreatedAt)
    push({
      at: ownerCreatedAt,
      icon: UserPlus,
      tone: "slate",
      title: "Owner added",
    });

  return events.sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
  );
}
